import { formatDateTime } from "features/discover/functions";
import type { UserNoteResponse } from "libs/apiClient";
import { useState } from "react";
import { UserNoteForm } from "./UserNoteForm";
import Modal from "../../../../../assets/js/components/Modal";
import client from "libs/client";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import Edit from "ui-kit/assets/edit/edit.svg";
import Delete from "ui-kit/assets/delete/delete.svg";
import "./admin-ui.scss";

interface UserNotesTableProps {
	userNotes: UserNoteResponse[];
}

export function UserNotesTable({ userNotes }: UserNotesTableProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [editingNote, setEditingNote] = useState<UserNoteResponse | null>(null);
	const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);

	const deleteUserNoteMutation = client.deleteAdminDiscoverUserNote.useMutation({
		onSuccess: () => {
			addSuccessToast(_("discover.userNote.toast.deleted"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverUserDetail"),
			});
			setDeletingNoteId(null);
		},
		onError: (res: { status?: number; message: string }) => {
			if (res.status !== 403) {
				addErrorToast(res.message);
			}
			setDeletingNoteId(null);
		},
	});

	const handleDelete = (noteId: number) => {
		if (!confirm(_("discover.userNote.deleteConfirm"))) {
			return;
		}
		setDeletingNoteId(noteId);
		deleteUserNoteMutation.mutate({ id: noteId });
	};

	const handleEdit = (note: UserNoteResponse) => {
		setEditingNote(note);
	};

	return (
		<>
			<table className="discover-table">
				<thead>
					<tr>
						<th>{_("discover.userNote.table.id")}</th>
						<th>{_("discover.userNote.table.note")}</th>
						<th>{_("discover.userNote.table.createdBy")}</th>
						<th>{_("discover.userNote.table.createdAt")}</th>
						<th>&nbsp;</th>
					</tr>
				</thead>
				<tbody>
					{userNotes.map((userNote) => (
						<tr key={userNote.id}>
							<td>{userNote.id}</td>
							<td>{userNote.note}</td>
							<td>
								{userNote.createdBy
									? `${userNote.createdBy.firstName ?? ""} ${userNote.createdBy.lastName ?? ""}`.trim() || "-"
									: "-"}
							</td>
							<td>{formatDateTime(userNote.createdAt.toString())}</td>
							<td>
								<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
									<button
										onClick={() => handleEdit(userNote)}
										disabled={deletingNoteId === userNote.id}
										className="admin-button-icon admin-button-icon--primary"
									>
										<img
											src={Edit}
											alt={_("discover.userNote.edit")}
											style={{
												width: "16px",
												height: "16px",
											}}
										/>
									</button>
									<button
										onClick={() => handleDelete(userNote.id)}
										disabled={deletingNoteId === userNote.id}
										className="admin-button-icon admin-button-icon--danger"
									>
										<img
											src={Delete}
											alt={_("discover.userNote.delete")}
											style={{
												width: "14px",
												height: "16px",
											}}
										/>
									</button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<Modal show={editingNote !== null} size="sm" title={_("discover.userNote.editNote")} position="top" onClose={() => setEditingNote(null)}>
				{editingNote && (
					<UserNoteForm
						key={editingNote.id}
						note={editingNote}
						mode="edit"
						setShowModal={(value) => {
							const show = typeof value === "function" ? value(editingNote !== null) : value;
							if (!show) setEditingNote(null);
						}}
					/>
				)}
			</Modal>
		</>
	);
}
